import { DateTime } from 'luxon';
import { computed, ComputedRef, Ref } from 'vue';
import { ConflictMarker } from '../Timeline.types';

type AllocationDateRange = {
    locationId: string;
    startDateTime: DateTime;
    endDateTime: DateTime;
};

/**
 * Composable that calculates conflict markers for timeline locations.
 * A conflict occurs when two or more allocations overlap on the same day.
 *
 * @returns A nested Map structure:
 *   - Outer Map key: Location ID
 *   - Inner Map key: ISO date string (YYYY-MM-DD)
 *   - Value: ConflictMarker indicating the conflict type for that day
 */
export function useConflictMarkers(
    allocations: Ref<AllocationDateRange[]>,
    startDateTime: Ref<DateTime | undefined>,
    endDateTime: Ref<DateTime | undefined>,
): ComputedRef<Map<string, Map<string, ConflictMarker>>> {
    const conflictMarkersByLocation = computed(() => {
        const result = new Map<string, Map<string, ConflictMarker>>();

        const timelineStart = startDateTime.value;
        const timelineEnd = endDateTime.value;
        if (!timelineStart || !timelineEnd) {
            return result;
        }

        const durationInDays = Math.round(timelineEnd.diff(timelineStart, 'days').days);
        if (durationInDays < 0) {
            throw new Error('Timeline end date must be after timeline start date');
        }
        if (durationInDays === 0) {
            return result;
        }

        // Pre-compute ISO date strings for each day in the timeline range
        const isoDateKeys: string[] = new Array(durationInDays);
        for (let dayIndex = 0; dayIndex < durationInDays; dayIndex++) {
            const isoDate = timelineStart.plus({ days: dayIndex }).toISODate();
            if (isoDate) {
                isoDateKeys[dayIndex] = isoDate;
            }
        }

        // Group allocations by location and convert to day indices
        const allocationsByLocation = new Map<string, { startDayIndex: number; endDayIndex: number }[]>();
        for (const allocation of allocations.value) {
            // Clamp allocation bounds to the visible timeline range
            const startDayIndex = Math.max(0, Math.floor(allocation.startDateTime.diff(timelineStart, 'days').days));
            const endDayIndex = Math.min(
                durationInDays,
                Math.ceil(allocation.endDateTime.diff(timelineStart, 'days').days),
            );

            if (startDayIndex >= endDayIndex) {
                continue; // Allocation outside timeline range
            }

            const existing = allocationsByLocation.get(allocation.locationId);
            if (existing) {
                existing.push({ startDayIndex, endDayIndex });
            } else {
                allocationsByLocation.set(allocation.locationId, [{ startDayIndex, endDayIndex }]);
            }
        }

        // Calculate conflict markers for each location
        for (const [locationId, locationAllocations] of allocationsByLocation) {
            // Early exit: conflicts require at least 2 allocations
            if (locationAllocations.length < 2) {
                continue;
            }

            const markers = calculateConflictMarkersWithSweepLine(locationAllocations, durationInDays, isoDateKeys);
            if (markers.size > 0) {
                result.set(locationId, markers);
            }
        }

        return result;
    });

    return conflictMarkersByLocation;
}

/**
 * Uses a sweep-line algorithm to detect overlapping allocations.
 * Generates boundary events at allocation start/end, then sweeps through
 * days counting active allocations to identify conflicts.
 */
function calculateConflictMarkersWithSweepLine(
    allocations: { startDayIndex: number; endDayIndex: number }[],
    durationInDays: number,
    isoDateKeys: string[],
): Map<string, ConflictMarker> {
    const markers = new Map<string, ConflictMarker>();

    // Create boundary events: +1 at start, -1 at end
    const boundaries: { dayIndex: number; delta: 1 | -1 }[] = [];
    for (const allocation of allocations) {
        boundaries.push({ dayIndex: allocation.startDayIndex, delta: 1 });
        boundaries.push({ dayIndex: allocation.endDayIndex, delta: -1 });
    }

    // Sort by day index
    boundaries.sort((a, b) => a.dayIndex - b.dayIndex);

    // Sweep through to find conflict ranges
    const hasConflictOnDay: boolean[] = new Array(durationInDays).fill(false);
    let activeAllocationCount = 0;
    let boundaryIndex = 0;

    for (let dayIndex = 0; dayIndex < durationInDays; dayIndex++) {
        // Process all boundaries at this day
        while (boundaryIndex < boundaries.length && boundaries[boundaryIndex].dayIndex === dayIndex) {
            activeAllocationCount += boundaries[boundaryIndex].delta;
            boundaryIndex++;
        }

        hasConflictOnDay[dayIndex] = activeAllocationCount >= 2;
    }

    // Convert conflict days to markers (start/middle/end/single)
    for (let dayIndex = 0; dayIndex < durationInDays; dayIndex++) {
        if (!hasConflictOnDay[dayIndex]) {
            continue;
        }

        const previousDayHasConflict = dayIndex > 0 && hasConflictOnDay[dayIndex - 1];
        const nextDayHasConflict = dayIndex < durationInDays - 1 && hasConflictOnDay[dayIndex + 1];
        const isoDateKey = isoDateKeys[dayIndex];

        if (!isoDateKey) {
            continue;
        }

        let marker: ConflictMarker;
        if (!previousDayHasConflict && !nextDayHasConflict) {
            marker = 'Conflict-Single';
        } else if (!previousDayHasConflict && nextDayHasConflict) {
            marker = 'Conflict-Start';
        } else if (previousDayHasConflict && nextDayHasConflict) {
            marker = 'Conflict-Middle';
        } else {
            marker = 'Conflict-End';
        }

        markers.set(isoDateKey, marker);
    }

    return markers;
}
